import React from 'react';
import { datesBetween, todayISO } from '../utils/billingPeriod';
import { classifySeverity } from '../utils/thresholds';
import { usePower } from '../state/PowerContext';

// Per-day "top hours" count. Captures the real demand window most days
// without cramming in the overnight lulls. Rare summer nights that stay
// hot and show up in the top 14 naturally surface here — nothing is
// filtered by wall-clock time, only by predicted value per day.
const TOP_N_PER_DAY = 14;

function fmtDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const mmdd = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${weekday} ${mmdd}`;
}

function fmtValue(v) {
  if (v == null) return '';
  if (v >= 10000) return Math.round(v / 100) / 10 + 'k';
  return Math.round(v).toLocaleString();
}

function fmtLmp(lmp) {
  if (lmp == null) return '';
  return `$${lmp.toFixed(lmp >= 100 ? 0 : 1)}`;
}

function hourLabel(h) {
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh}${ampm}`;
}

export default function HourGrid({ records, high }) {
  const { activePeriod, series } = usePower();

  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  })();
  const visibleStart = yesterday > activePeriod.startISO ? yesterday : activePeriod.startISO;
  const today = todayISO();

  // Bucket records by date once, keeping only dates that actually have data.
  const byDate = new Map();
  for (const r of records) {
    if (r.date < visibleStart || r.date > activePeriod.endISO) continue;
    if (!byDate.has(r.date)) byDate.set(r.date, []);
    byDate.get(r.date).push(r);
  }
  const dates = Array.from(byDate.keys()).sort();

  // Per-day top-N by (series) value, ordered chronologically for display
  const topPerDay = new Map();
  for (const d of dates) {
    const all = byDate.get(d);
    const sorted = [...all].sort((a, b) => b.value - a.value).slice(0, TOP_N_PER_DAY);
    sorted.sort((a, b) => a.hour - b.hour);
    topPerDay.set(d, sorted);
  }

  return (
    <div className="panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          Hourly forecast · {series} · top {TOP_N_PER_DAY}/day by predicted value
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
          red = ≥95% of period high · amber = ≥90% · cells are chronological per row
        </span>
      </div>

      <div className="grid-wrap">
        <table className="hgrid hgrid-topn">
          <tbody>
            {dates.map(d => {
              const row = topPerDay.get(d) || [];
              if (row.length === 0) return null;
              const past = d < today;
              return (
                <tr key={d} className={past ? 'past' : ''}>
                  <td className="day-label">{fmtDate(d)}</td>
                  {row.map(r => {
                    const sev = classifySeverity(r.value, high?.value);
                    const isPeak = high && r.date === high.date && r.hour === high.hour;
                    const tempStr = r.temperature != null ? `${Math.round(r.temperature)}°F` : null;
                    const tip = `${d} · ${hourLabel(r.hour)}\n${r.value.toFixed(1)} ${series} (${sev}${r.isForecast ? ', forecast' : ', actual'})${r.lmp != null ? `\n$${r.lmp.toFixed(2)}/MWh` : ''}${tempStr ? `\n${tempStr}` : ''}`;
                    return (
                      <td
                        key={r.hour}
                        className={`cell topn ${sev} ${isPeak ? 'peak' : ''} ${r.isForecast ? 'forecast' : 'actual'}`}
                        title={tip}
                      >
                        <div className="when">{hourLabel(r.hour)}</div>
                        <div className="val">{fmtValue(r.value)}</div>
                        {r.lmp != null && <div className="lmp">{fmtLmp(r.lmp)}</div>}
                        {tempStr && <div className="temp">{tempStr}</div>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
